import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Crown, ShieldCheck, Info, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier visual config — Tropical Luxury palette (Navy + Gold)
const TIER_STYLES = {
  platinum: {
    bg: 'bg-gradient-to-br from-[#D4AF37] to-[#C8A028] text-[#0B192C]',
    ring: 'ring-[#D4AF37]',
    icon: Crown,
    accent: 'text-[#D4AF37]',
  },
  gold: {
    bg: 'bg-[#D4AF37] text-[#0B192C]',
    ring: 'ring-[#D4AF37]/60',
    icon: ShieldCheck,
    accent: 'text-[#D4AF37]',
  },
  silver: {
    bg: 'bg-slate-300 text-[#0B192C]',
    ring: 'ring-slate-300',
    icon: ShieldCheck,
    accent: 'text-slate-500',
  },
  building: {
    bg: 'bg-white border border-slate-300 text-slate-600',
    ring: 'ring-slate-200',
    icon: Sparkles,
    accent: 'text-slate-400',
  },
};

const tierLabelEs = {
  platinum: 'Platino',
  gold: 'Oro',
  silver: 'Plata',
  building: 'Construyendo Confianza',
};

/**
 * TrustScore - displays agent's WakaCasa Trust Score (0-100).
 *
 * Variants:
 *   pill    - small inline pill (e.g. property detail owner block)
 *   large   - prominent circular score on agent profile
 *
 * If `data` prop is passed, renders directly (no fetch).
 * Otherwise fetches GET /api/agents/{userId}/trust-score on mount.
 * Click opens breakdown dialog.
 */
export default function TrustScore({ userId, data, variant = 'pill', testIdPrefix = 'trust' }) {
  const { language } = useLanguage();
  const [score, setScore] = useState(data || null);
  const [loading, setLoading] = useState(!data);
  const [open, setOpen] = useState(false);

  const tt = (en, es) => (language === 'es' ? es : en);

  useEffect(() => {
    if (data) {
      setScore(data);
      setLoading(false);
      return;
    }
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/agents/${userId}/trust-score`)
      .then(res => setScore(res.data))
      .catch(() => setScore(null))
      .finally(() => setLoading(false));
  }, [userId, data]);

  // Lazy-load full breakdown if dialog opens but only summary was passed in `data`
  useEffect(() => {
    if (!open) return;
    if (!score || (score.breakdown && score.breakdown.length > 0)) return;
    if (!userId) return;
    axios.get(`${API}/agents/${userId}/trust-score`)
      .then(res => setScore(res.data))
      .catch(() => {});
  }, [open, score, userId]);

  if (loading) {
    return variant === 'large' ? (
      <div className="w-28 h-28 rounded-full bg-slate-100 animate-pulse" />
    ) : (
      <div className="h-6 w-20 rounded-full bg-slate-100 animate-pulse" />
    );
  }

  if (!score || score.user_found === false) return null;

  const tier = TIER_STYLES[score.tier] || TIER_STYLES.building;
  const Icon = tier.icon;
  const tierLabel = language === 'es' ? (tierLabelEs[score.tier] || score.tier_label) : score.tier_label;

  // ===== Pill variant =====
  if (variant === 'pill') {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tier.bg} hover:scale-105 transition-transform shadow-sm`}
          data-testid={`${testIdPrefix}-pill`}
          aria-label={tt(`Trust Score ${score.score} of 100, ${tierLabel}`, `Puntaje de Confianza ${score.score} de 100, ${tierLabel}`)}
        >
          <Icon className="w-3 h-3" />
          <span data-testid={`${testIdPrefix}-pill-score`}>{score.score}</span>
          <span className="opacity-70 font-normal">/100</span>
        </button>
        <BreakdownDialog open={open} onOpenChange={setOpen} score={score} tier={tier} tierLabel={tierLabel} testIdPrefix={testIdPrefix} />
      </>
    );
  }

  // ===== Large variant =====
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex items-center gap-4 text-left"
        data-testid={`${testIdPrefix}-large`}
      >
        <div className={`relative w-24 h-24 rounded-full ring-4 ${tier.ring} ${tier.bg} flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
          <div className="text-center leading-none">
            <div className="font-heading text-3xl font-bold" data-testid={`${testIdPrefix}-large-score`}>{score.score}</div>
            <div className="text-[9px] opacity-70 mt-0.5">/ 100</div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <Icon className={`w-4 h-4 ${tier.accent}`} />
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500">
              {tt('Trust Score', 'Puntaje de Confianza')}
            </span>
          </div>
          <div className="font-heading font-bold text-lg text-[#0B192C]" data-testid={`${testIdPrefix}-large-tier`}>
            {tierLabel}
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-slate-500 mt-0.5 group-hover:text-[#D4AF37] transition-colors">
            <Info className="w-3 h-3" />
            {tt('See breakdown', 'Ver desglose')}
          </div>
        </div>
      </button>
      <BreakdownDialog open={open} onOpenChange={setOpen} score={score} tier={tier} tierLabel={tierLabel} testIdPrefix={testIdPrefix} />
    </>
  );
}

function BreakdownDialog({ open, onOpenChange, score, tier, tierLabel, testIdPrefix }) {
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const Icon = tier.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-breakdown-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            {tt('WakaCasa Trust Score', 'Puntaje de Confianza WakaCasa')}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {tt(
              'A transparent 0–100 score combining verification, transactions, profile, and engagement.',
              'Un puntaje transparente de 0–100 que combina verificación, transacciones, perfil y engagement.'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Hero score */}
        <div className={`rounded-xl p-4 flex items-center gap-4 ${tier.bg}`}>
          <div className="font-heading text-4xl font-bold leading-none">
            {score.score}
            <span className="text-base font-normal opacity-70 ml-1">/100</span>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider opacity-70">
              <Icon className="w-3.5 h-3.5" />
              {tt('Tier', 'Nivel')}
            </div>
            <div className="font-heading font-bold text-lg">{tierLabel}</div>
          </div>
        </div>

        {/* Breakdown rows */}
        <div className="space-y-3 mt-1">
          {(score.breakdown || []).map((b) => (
            <div key={b.key} data-testid={`${testIdPrefix}-row-${b.key}`}>
              <div className="flex items-center justify-between mb-1 text-sm">
                <span className="font-medium text-slate-800">
                  {language === 'es' ? b.label_es || b.label : b.label}
                </span>
                <span className="font-semibold text-slate-900 tabular-nums">
                  <span className={b.points === b.max ? 'text-[#D4AF37]' : 'text-slate-700'}>{b.points}</span>
                  <span className="text-slate-400"> / {b.max}</span>
                </span>
              </div>
              <Progress value={(b.points / b.max) * 100} className="h-1.5" />
              <p className="text-[11px] text-slate-500 mt-1">
                {language === 'es' ? b.detail_es || b.detail : b.detail}
              </p>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-slate-400 text-center mt-1">
          {tt(
            'Updated in real-time. Tiers: Platinum 90+, Gold 75+, Silver 50+.',
            'Actualizado en tiempo real. Niveles: Platino 90+, Oro 75+, Plata 50+.'
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
